import React, { MouseEvent, useCallback, useState } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { roleLabels } from '../../../constants/roleLabels';
import { IconButton } from '../../../shared/button/IconButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Delete, Duplicate, Ellipsis, Mail, User } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import styles from '../Clients.module.scss';
import { Position } from '../../../shared/modal/PositionModal';
import { ClientUser, duplicateUser, getConfirmDeleteUser, getModalOpen as getUsersModalOpen, toggleDeleteUserConfirmationModal, toggleUsersModal } from '../../users/store';
import { resetClientUserPassword } from '../store';
import { UpsertUserModal } from '../../users/UpsertUserModal';
import { ActionModal } from '../../../shared/modal/ActionModal';
import { DeleteConfirmationModal } from '../../users/DeleteConfirmationModal';

const { white, lightestGrey } = styles;

interface ClientUsersProps {
    users: ClientUser[];
    clientId?: number;
    clientName: string;
}

export const ClientUsers: React.FC<ClientUsersProps> = ({ users, clientId, clientName }) => {
    const [userActionModalPosition, setUserActionModalPosition] = useState<Position | null>(null);
    const [userActionModalOpen, setUserActionModalOpen] = useState<ClientUser | null>(null);
    const dispatch = useAppDispatch();
    const userModalOpen = useAppSelector(getUsersModalOpen);

    const openUserActionModal = useCallback((x: number, y: number, user: ClientUser) => {
        setUserActionModalPosition({ x, y });
        setUserActionModalOpen(user);
    }, []);

    const closeUserActionModal = useCallback(() => {
        setUserActionModalPosition(null);
        setUserActionModalOpen(null);
    }, []);

    const editUser = useCallback((user: ClientUser) => dispatch(toggleUsersModal(true, user)), [dispatch]);

    const closeUserModal = useCallback(() => dispatch(toggleUsersModal(false)), [dispatch]);

    const confirmDeleteUser = useAppSelector(getConfirmDeleteUser);
    const openDeleteUserConfirmationModal = useCallback((user: ClientUser) => dispatch(toggleDeleteUserConfirmationModal(user)), [dispatch]);
    const closeDeleteUserConfirmationModal = useCallback(() => dispatch(toggleDeleteUserConfirmationModal(null)), [dispatch]);
    const resetPassword = useCallback((username: string) => dispatch(resetClientUserPassword(username)), [dispatch]);
    const duplicateSelectedUser = useCallback((user: ClientUser) => dispatch(duplicateUser(user)), [dispatch]);

    const userModalActions = useCallback((user: ClientUser) => [
        { label: 'Open', icon: User, onClick: () => editUser(user) },
        { label: 'Reset Password', icon: Mail, onClick: () => resetPassword(user.username), minWidth: '100px' },
        { label: 'Duplicate', icon: Duplicate, onClick: () => duplicateSelectedUser(user), withSeparator: true },
        { label: 'Delete', icon: Delete, onClick: () => openDeleteUserConfirmationModal(user), isDelete: true }
    ], [editUser, openDeleteUserConfirmationModal, resetPassword, duplicateSelectedUser]);

    const createNewUser = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        dispatch(toggleUsersModal(true, undefined, clientId));
    }, [dispatch, clientId]);

    const userHeaders = [{ name: 'Username', width: '50%' }, { name: 'Role', width: '45%' }, { name: '', width: '5%' }];

    return (
        <>
            <div className={styles.clientModalContentWrapper}>
                <div className={styles.clientModalHeader}>
                    <div className={styles.clientModalTitle}>Users</div>
                    <PlusButton onClick={createNewUser} disabled={!clientId} fontSize={16} />
                </div>
                <>
                    <div className={styles.tableHeader}>
                        {userHeaders.map(({ name, width }) => (
                            <div key={name} className={styles.headerCell} style={{ width }}>{name}</div>
                        ))}
                    </div>
                    <div className={styles.tableWrapper}>
                        <Scrollable>
                            {users.map((user, index) => (
                                <div className={styles.tableRow} key={index} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                    <div className={styles.titleCell} style={{ width: '50%' }}>{user.username}</div>
                                    <div className={styles.role} style={{ width: '45%' }}>{roleLabels[user.role]}</div>
                                    <div className={styles.toggleCell} style={{ width: '5%' }}>
                                        <IconButton icon={Ellipsis} fontSize={19} onClick={e => openUserActionModal(e.clientX, e.clientY, user)} />
                                    </div>
                                </div>
                            ))}
                        </Scrollable>
                    </div>
                </>
            </div>
            <UpsertUserModal
                isOpen={userModalOpen}
                closeModal={closeUserModal}
                clientName={clientName}
                clientView
            />
            {userActionModalOpen &&
                <ActionModal
                    actions={userModalActions(userActionModalOpen)}
                    closeModal={closeUserActionModal}
                    isOpen={!isNull(userActionModalPosition)}
                    position={userActionModalPosition}
                    testId='client-users'
                />
            }
            {!isNull(confirmDeleteUser) &&
                <DeleteConfirmationModal
                    isOpen
                    closeModal={closeDeleteUserConfirmationModal}
                    user={confirmDeleteUser}
                    clientId={clientId}
                    clientName={clientName}
                />
            }
        </>
    );
};
